import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  ApiUserInline,
  ApiVenueAllStaffData,
  ApiVenueDetail,
  ApiVenueStaffInfo,
  ApiVenueStaffInfoWithVenue,
  VenueRole,
} from "griptonite-api-client";
import { orderBy } from "lodash";
import { useCallback } from "react";

import queryClient from "@/core/queryClient";

import api from "../core/api";

export const availableVenuesQuery = queryOptions({
  queryKey: ["venues"],
  queryFn: async () => {
    const response = await api.venues.getVenues(true);
    return orderBy(response.data, "name");
  },
});

export const useAvailableVenuesQuery = <TData = ApiVenueDetail[]>(
  select?: (venues: ApiVenueDetail[]) => TData,
) =>
  useQuery({
    ...availableVenuesQuery,
    select,
  });

export const useAllVenuesQuery = <TData = ApiVenueDetail[]>(
  select?: (venues: ApiVenueDetail[]) => TData,
) =>
  useQuery({
    queryKey: ["allVenues"],
    queryFn: async () => {
      const response = await api.venues.getVenues();
      return orderBy(response.data, "name");
    },
    select,
  });

export const useAllVenuesByUuid = () => {
  const select = useCallback(
    (venues: ApiVenueDetail[]) => Object.fromEntries(venues.map((v) => [v.uuid, v])),
    [],
  );
  return useAllVenuesQuery(select).data;
};

export const useVenueQuery = <TData = ApiVenueDetail>(
  uuid?: string,
  select?: (venue: ApiVenueDetail) => TData,
) =>
  useQuery({
    queryKey: ["venues", uuid],
    queryFn: () => api.venues.getVenue(uuid!),
    enabled: !!uuid,
    select,
  });

export const useCurrentVenueUuid = () => {
  const { venueUuid }: { venueUuid: string | undefined } = useParams({
    strict: false,
  });
  return venueUuid;
};

export const useCurrentVenueQuery = () => {
  const venueUuid = useCurrentVenueUuid();
  return useVenueQuery(venueUuid);
};

export const useCurrentClimbType = () => {
  const { climbType }: { climbType: string | undefined } = useParams({
    strict: false,
  });
  if (!climbType || !["BOULDER", "SPORT"].includes(climbType)) {
    return undefined;
  }
  return climbType as "BOULDER" | "SPORT";
};

export const useCurrentVenueMapsByUuidQuery = () => {
  const venueUuid = useCurrentVenueUuid();
  return useVenueQuery(venueUuid, (venue) =>
    Object.fromEntries(venue.maps.map((m) => [m.uuid, m])),
  );
};

export const useVenueStaffQuery = <TData = ApiVenueStaffInfo[]>(
  uuid?: string,
  select?: (staff: ApiVenueStaffInfo[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", uuid, "staff"],
    queryFn: async () => {
      const response = await api.venues.getVenueStaff(uuid!);
      return response.data;
    },
    enabled: !!uuid,
    select,
  });
};

export const useActiveVenueStaffQuery = (uuid?: string) => {
  const select = useCallback((staff: ApiVenueStaffInfo[]) => staff.filter((s) => s.isActive), []);
  return useVenueStaffQuery(uuid, select);
};

export const useActiveVenueStaffUsersByUuid = (uuid?: string) => {
  const select = useCallback((staff: ApiVenueStaffInfo[]) => {
    return Object.fromEntries(staff.filter((s) => s.isActive).map((s) => [s.user.uuid, s.user]));
  }, []);
  return useVenueStaffQuery(uuid, select).data;
};

export const useVenueColorsQuery = (uuid?: string) => {
  return useQuery({
    queryKey: ["venues", uuid, "colors"],
    queryFn: async () => {
      const response = await api.venues.getVenueColors(uuid!);
      return response.data;
    },
    enabled: !!uuid,
  });
};

export const useDeleteVenueColorMutation = (venueUuid?: string) => {
  return useMutation({
    mutationFn: async (colorUuid: string) => {
      if (!venueUuid) {
        throw new Error("Missing venue uuid");
      }
      return api.venues.deleteVenueColor(venueUuid, colorUuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "colors"],
      });
    },
  });
};

export const useAllVenueStaff = <TData = ApiVenueAllStaffData>(
  select?: (data: ApiVenueAllStaffData) => TData,
  venues?: string[],
) => {
  return useQuery({
    queryKey: ["venues", "allStaff", ...(venues ? venues.join(",") : [])],
    queryFn: () => api.venues.getAllVenuesStaff(venues),
    select,
  });
};

export const useAllVenueStaffData = ({ venues }: { venues?: string[] } = {}) => {
  const select = useCallback(({ staff: allRoles, venues }: ApiVenueAllStaffData) => {
    const staffMap: Map<
      string,
      {
        user: ApiUserInline & { email: string };
        venueRoles: Map<string, Omit<ApiVenueStaffInfo, "user">>;
        hasAnyActiveRole: boolean;
      }
    > = new Map();
    allRoles.forEach((role) => {
      if (!staffMap.has(role.user.uuid)) {
        staffMap.set(role.user.uuid, {
          user: role.user,
          venueRoles: new Map(),
          hasAnyActiveRole: false,
        });
      }
      const staffItem = staffMap.get(role.user.uuid)!;
      const { user: _, ...roleWithoutUser } = role;
      staffItem.venueRoles.set(roleWithoutUser.venue, roleWithoutUser);
      staffItem.hasAnyActiveRole = staffItem.hasAnyActiveRole || role.isActive;
    });
    const staff = orderBy(
      Array.from(staffMap.values()),
      ["hasAnyActiveRole", "user.firstName", "user.lastName"],
      ["desc", "asc", "asc"],
    );
    return { venues, staff };
  }, []);

  return useAllVenueStaff(select, venues);
};

export const useUpdateStaffRoleMutation = () => {
  return useMutation({
    mutationFn: async ({
      venueUuid,
      userUuid,
      role,
      isActive,
    }: {
      venueUuid: string;
      userUuid: string;
      role?: VenueRole;
      isActive?: boolean;
    }) => {
      return api.venues.updateStaffRole(venueUuid, userUuid, {
        role,
        isActive,
      });
    },
    onMutate: ({ venueUuid, userUuid, role, isActive }) => {
      let previousData: ApiVenueStaffInfoWithVenue | undefined;
      let shouldRevalidate = false;
      queryClient.setQueryData(
        ["venues", "allStaff"],
        (oldData: ApiVenueAllStaffData | undefined) => {
          if (!oldData) {
            shouldRevalidate = true;
            return;
          }
          previousData = oldData.staff.find(
            (s) => s.user.uuid === userUuid && s.venue === venueUuid,
          );
          if (!oldData.staff.find((s) => s.user.uuid === userUuid && s.venue === venueUuid)) {
            shouldRevalidate = true;
            return;
          }
          return {
            ...oldData,
            staff: oldData.staff.map((s) =>
              s.user.uuid === userUuid && s.venue === venueUuid
                ? {
                    ...s,
                    role: role ?? previousData?.role,
                    isActive: isActive ?? previousData?.isActive,
                  }
                : s,
            ),
          };
        },
      );
      return { previousData, shouldRevalidate };
    },
    onSuccess: (_e, _v, context) => {
      if (context.shouldRevalidate) {
        queryClient.invalidateQueries({
          queryKey: ["venues", "allStaff"],
        });
      }
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "venues" && q.queryKey[2] === "staff",
      });
    },
    onError: (_error, variables, context) => {
      if (!context?.previousData) {
        return;
      }
      queryClient.setQueryData(
        ["venues", "allStaff"],
        (oldData: ApiVenueAllStaffData | undefined) => {
          if (!oldData) {
            return;
          }
          return {
            ...oldData,
            staff: oldData.staff.map((s) =>
              s.user.uuid === variables.userUuid && s.venue === variables.venueUuid
                ? { ...s, ...context.previousData }
                : s,
            ),
          };
        },
      );
    },
  });
};

export const useUpdateAllUserRolesMutation = () => {
  return useMutation({
    mutationFn: async ({
      userUuid,
      role,
      isActive,
      venues,
    }: {
      userUuid: string;
      role?: VenueRole;
      isActive?: boolean;
      venues?: string[];
    }) => {
      return api.venues.updateAllUserRoles(userUuid, {
        role,
        isActive,
        venues,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["venues", "allStaff"],
      });
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "venues" && q.queryKey[2] === "staff",
      });
    },
  });
};
